import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import { useEffect, useState } from 'react';

import { ColumnCard } from 'src/components/Card/ColumnCard';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { VERIFICATIONS } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsWidgetCallToAction } from 'src/modules/analytics/types';

import translations from './.vocab';
import { VerificationsNudge } from './Nudges/VerificationsNudge';
import {
  VerificationsDrawer,
  type VerificationsDrawerTab,
} from './VerificationsDrawer';
import {
  getAnalyticsWidgetName,
  verificationsWidgetCallToActionToLinkText,
} from './analyticsUtils';
import { useVerifications } from './data';

export const Verifications = () => {
  const { t } = useTranslations(translations);

  const { verificationsWidgetViewed, verificationsWidgetClicked } =
    useAnalytics();

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [activeDrawerTab, setActiveDrawerTab] =
    useState<VerificationsDrawerTab>('verified');
  const resetActiveDrawerTab = () => setActiveDrawerTab('verified');

  const { verifiedCredentialCount, nudge, loading, error } = useVerifications();
  const showLoading = Boolean(loading || error);

  const hasVerifiedVerifications =
    (verifiedCredentialCount && verifiedCredentialCount > 0) || false;

  const openDefaultDrawerTab = () => {
    resetActiveDrawerTab();
    setShowDrawer(true);
  };

  const openVerifiableDrawerTab = () => {
    setActiveDrawerTab('verifiable');
    setShowDrawer(true);
  };

  const verificationCtaAnalyticsOnClick = ({
    callToAction,
  }: {
    callToAction: AnalyticsVerificationsWidgetCallToAction;
  }) => {
    const linkText = verificationsWidgetCallToActionToLinkText[callToAction];
    const widgetName = nudge ? getAnalyticsWidgetName(nudge) : undefined;
    verificationsWidgetClicked({
      callToAction,
      widgetName,
      linkText,
    });
  };

  const cardButtonOnClick = () => {
    verificationCtaAnalyticsOnClick({ callToAction: 'view_verifications' });

    openDefaultDrawerTab();
  };

  const loader = (
    <Stack space="medium">
      <LoadingItem type="text" size="standard" width="medium" />
      <LoadingItem type="text" size="standard" width="large" lines={2} />
      <LoadingItem type="button" />
    </Stack>
  );

  const cardButtonProps = {
    onClick: cardButtonOnClick,
    label: t('See verifications'),
  };

  const unverifiedCardContent = <Text>{t('No verifications')}</Text>;

  const verifiedCardContent = verifiedCredentialCount ? (
    <Text>{t('N verifications', { count: verifiedCredentialCount })}</Text>
  ) : null;

  useEffect(() => {
    const widgetName = nudge ? getAnalyticsWidgetName(nudge) : undefined;
    verificationsWidgetViewed({ widgetName });
    // Only send tracking once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ColumnCard
        name={VERIFICATIONS}
        title={t('Verifications')}
        loader={loader}
        loading={showLoading}
        buttonProps={cardButtonProps}
      >
        <Stack space="large">
          {hasVerifiedVerifications
            ? verifiedCardContent
            : unverifiedCardContent}
          {nudge ? (
            <VerificationsNudge
              nudge={nudge}
              openVerifiableTab={openVerifiableDrawerTab}
              analyticsOnClick={verificationCtaAnalyticsOnClick}
            />
          ) : null}
        </Stack>
      </ColumnCard>
      <VerificationsDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        activeTab={activeDrawerTab}
        setActiveTabFn={setActiveDrawerTab}
      />
    </>
  );
};
